import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import {
  closeAccessCase,
  disableAdmin2fa,
  enableAdmin2fa,
  fetchAccessCases,
  fetchAdminSecurityStatus,
  fetchBackupStatus,
  fetchEmailFailures,
  fetchSystemDiagnostics,
  fetchSystemHealth,
  fetchSystemLogs,
  openAccessCase,
  setupAdmin2fa,
} from './api';
import type { AdminSecurityStatus, PlatformAccessCase, SystemHealth } from './types';
import { fmtDate, statusStyle } from './utils';

type Tab = 'health' | 'email' | 'logs' | 'access' | 'backups' | 'security';

const statusColor = (s: string) =>
  s === 'healthy' || s === 'operational' || s === 'ok' ? '#34d399'
    : s === 'degraded' ? '#fbbf24' : '#f87171';

const SystemPage: React.FC = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) || 'health';

  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [diagnostics, setDiagnostics] = useState<Record<string, unknown> | null>(null);
  const [emailFailures, setEmailFailures] = useState<any[]>([]);
  const [adminLogs, setAdminLogs] = useState<any[]>([]);
  const [backups, setBackups] = useState<any>(null);
  const [cases, setCases] = useState<PlatformAccessCase[]>([]);
  const [ttlHours, setTtlHours] = useState(4);
  const [loading, setLoading] = useState(true);
  const [caseForm, setCaseForm] = useState({ business_id: '', reason: '', purpose: 'support' });
  const [security, setSecurity] = useState<AdminSecurityStatus | null>(null);
  const [twoFaSetup, setTwoFaSetup] = useState<{ secret: string; qr_data_url: string } | null>(null);
  const [twoFaCode, setTwoFaCode] = useState('');
  const [securityBusy, setSecurityBusy] = useState(false);

  const setTab = (t: Tab) => setSearchParams({ tab: t }, { replace: true });

  const loadCore = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [h, d] = await Promise.all([
        fetchSystemHealth(token),
        fetchSystemDiagnostics(token),
      ]);
      setHealth(h);
      setDiagnostics(d);
    } catch {
      toast.error('Failed to load system health');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadCore(); }, [loadCore]);

  useEffect(() => {
    if (!token) return;
    if (tab === 'email') fetchEmailFailures(token).then(setEmailFailures).catch(() => {});
    if (tab === 'logs') fetchSystemLogs(token, 150).then(setAdminLogs).catch(() => {});
    if (tab === 'backups') fetchBackupStatus(token).then(setBackups).catch(() => {});
    if (tab === 'access') {
      fetchAccessCases(token, true).then((r) => { setCases(r.cases); setTtlHours(r.ttlHours); }).catch(() => {});
    }
    if (tab === 'security') {
      fetchAdminSecurityStatus(token).then(setSecurity).catch(() => toast.error('Failed to load security settings'));
    }
  }, [token, tab]);

  const onOpenCase = async () => {
    if (!token) return;
    const businessId = parseInt(caseForm.business_id, 10);
    if (Number.isNaN(businessId) || !caseForm.reason.trim()) {
      return toast.error('Business ID and reason required');
    }
    try {
      await openAccessCase(token, {
        business_id: businessId,
        reason: caseForm.reason.trim(),
        purpose: caseForm.purpose,
      });
      toast.success(`Access case opened (${ttlHours}h)`);
      setCaseForm({ business_id: '', reason: '', purpose: 'support' });
      const r = await fetchAccessCases(token, true);
      setCases(r.cases);
    } catch {
      toast.error('Failed to open case');
    }
  };

  const onCloseCase = async (id: string) => {
    if (!token) return;
    try {
      await closeAccessCase(token, id);
      toast.success('Case closed');
      const r = await fetchAccessCases(token, true);
      setCases(r.cases);
    } catch {
      toast.error('Failed to close case');
    }
  };

  const onStart2fa = async () => {
    if (!token) return;
    setSecurityBusy(true);
    try {
      const setup = await setupAdmin2fa(token);
      setTwoFaSetup(setup);
      setTwoFaCode('');
      toast.info('Scan the QR code, then enter a code to confirm.');
    } catch {
      toast.error('Failed to start 2FA setup');
    } finally {
      setSecurityBusy(false);
    }
  };

  const onEnable2fa = async () => {
    if (!token || !twoFaCode.trim()) return toast.error('Enter the 6-digit code');
    setSecurityBusy(true);
    try {
      await enableAdmin2fa(token, twoFaCode.trim());
      toast.success('Two-factor authentication enabled');
      setTwoFaSetup(null);
      setTwoFaCode('');
      setSecurity(await fetchAdminSecurityStatus(token));
    } catch (e: any) {
      toast.error(e.message || 'Invalid code');
    } finally {
      setSecurityBusy(false);
    }
  };

  const onDisable2fa = async () => {
    if (!token || !twoFaCode.trim()) return toast.error('Enter your current 6-digit code');
    setSecurityBusy(true);
    try {
      await disableAdmin2fa(token, twoFaCode.trim());
      toast.success('Two-factor authentication disabled');
      setTwoFaSetup(null);
      setTwoFaCode('');
      setSecurity(await fetchAdminSecurityStatus(token));
    } catch (e: any) {
      toast.error(e.message || 'Invalid code');
    } finally {
      setSecurityBusy(false);
    }
  };

  if (loading && !health) return <div className="plat-empty">Loading system status…</div>;

  return (
    <>
      <div className="plat-header">
        <div>
          <h2>System health</h2>
          <p>Services, email failures, admin logs, backups, and sensitive access cases.</p>
        </div>
        <button type="button" className="plat-btn plat-btn-ghost" onClick={loadCore}>Refresh</button>
      </div>

      {health && (
        <div className="plat-kpi-row">
          <div className="plat-kpi"><div className="kl">Overall</div><div className="kv" style={{ color: statusColor(health.status), fontSize: 22 }}>{health.status}</div></div>
          <div className="plat-kpi"><div className="kl">Uptime</div><div className="kv" style={{ fontSize: 22 }}>{health.uptime}</div></div>
          <div className="plat-kpi warn"><div className="kl">Failed emails</div><div className="kv">{health.messaging.failedEmailCount}</div></div>
          <div className="plat-kpi warn"><div className="kl">Blocked (24h)</div><div className="kv">{health.security.unauthorizedAttempts24h}</div></div>
          <div className="plat-kpi"><div className="kl">DB</div><div className="kv" style={{ fontSize: 18, color: statusColor(health.database.status) }}>{health.database.status}</div></div>
        </div>
      )}

      <div className="plat-pills" style={{ marginBottom: 18 }}>
        {(['health', 'email', 'logs', 'access', 'backups', 'security'] as Tab[]).map((t) => (
          <button key={t} type="button" className={`plat-pill${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'health' && health && (
        <>
          <div className="plat-panel">
            <h3>Integrations</h3>
            <div className="plat-table-wrap">
              <table className="plat-table">
                <thead><tr><th>Service</th><th>Status</th></tr></thead>
                <tbody>
                  {health.integrations.map((i) => (
                    <tr key={i.key}>
                      <td>{i.label}</td>
                      <td style={{ color: statusColor(i.status), fontWeight: 700 }}>{i.configured ? 'Configured' : 'Missing'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {diagnostics && (
            <div className="plat-panel">
              <h3>Platform diagnostics</h3>
              <div className="plat-detail-row"><span className="label">Total users</span><span>{String((diagnostics as any).totalUsers ?? '—')}</span></div>
              <div className="plat-detail-row"><span className="label">Scheduled erasure</span><span>{String((diagnostics as any).scheduledErasure ?? '—')}</span></div>
              <div className="plat-detail-row"><span className="label">Admin log entries</span><span>{String((diagnostics as any).adminLogEntries ?? '—')}</span></div>
              <div className="plat-detail-row"><span className="label">Memory (heap)</span><span>{health.memory.heapUsedMb} / {health.memory.heapTotalMb} MB</span></div>
              <div className="plat-detail-row"><span className="label">Environment</span><span>{health.environment}</span></div>
            </div>
          )}
        </>
      )}

      {tab === 'email' && (
        <div className="plat-panel" style={{ padding: 0, overflow: 'hidden' }}>
          {emailFailures.length === 0 ? (
            <div className="plat-empty">No failed email messages in queue.</div>
          ) : (
            <div className="plat-table-wrap">
              <table className="plat-table">
                <thead>
                  <tr><th>When</th><th>Business</th><th>Subject</th><th>Error</th><th>Tries</th></tr>
                </thead>
                <tbody>
                  {emailFailures.map((row) => (
                    <tr key={row.id}>
                      <td className="plat-muted">{fmtDate(row.updated_at || row.created_at)}</td>
                      <td>{row.business_name || `#${row.business_id}`}</td>
                      <td>{row.subject || '—'}</td>
                      <td className="plat-muted" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.error_message}>{row.error_message || '—'}</td>
                      <td>{row.attempts ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'logs' && (
        <div className="plat-panel" style={{ padding: 0, overflow: 'hidden' }}>
          {adminLogs.length === 0 ? (
            <div className="plat-empty">No admin log entries.</div>
          ) : (
            <div className="plat-table-wrap">
              <table className="plat-table">
                <thead><tr><th>When</th><th>Type</th><th>Who</th><th>Action</th><th>Endpoint</th></tr></thead>
                <tbody>
                  {adminLogs.map((log: any) => (
                    <tr key={log.id}>
                      <td className="plat-muted">{fmtDate(log.created_at)}</td>
                      <td><span style={{ color: log.type === 'unauthorized_access_attempt' ? '#f87171' : '#a5b4fc', fontWeight: 700 }}>{log.type}</span></td>
                      <td>{log.email || '—'}</td>
                      <td>{log.action || '—'}</td>
                      <td className="plat-muted" style={{ fontSize: 12 }}>{log.endpoint}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'access' && (
        <>
          <div className="plat-panel">
            <h3>Open sensitive access case</h3>
            <p className="plat-muted" style={{ marginTop: 0 }}>
              Required for tenant-scoped DSAR exports. Cases expire after {ttlHours} hours.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
              <input className="plat-search" placeholder="Business ID" value={caseForm.business_id} onChange={(e) => setCaseForm((s) => ({ ...s, business_id: e.target.value }))} />
              <input className="plat-search" placeholder="Reason *" value={caseForm.reason} onChange={(e) => setCaseForm((s) => ({ ...s, reason: e.target.value }))} />
              <select className="plat-select" value={caseForm.purpose} onChange={(e) => setCaseForm((s) => ({ ...s, purpose: e.target.value }))}>
                <option value="support">Support</option>
                <option value="billing">Billing</option>
                <option value="dsar">DSAR / legal</option>
                <option value="security">Security</option>
              </select>
            </div>
            <button type="button" className="plat-btn plat-btn-primary" onClick={onOpenCase}>Open case</button>
          </div>
          <div className="plat-panel" style={{ padding: 0, overflow: 'hidden' }}>
            {cases.length === 0 ? (
              <div className="plat-empty">No access cases.</div>
            ) : (
              <div className="plat-table-wrap">
                <table className="plat-table">
                  <thead><tr><th>Business</th><th>Operator</th><th>Purpose</th><th>Reason</th><th>Expires</th><th></th></tr></thead>
                  <tbody>
                    {cases.map((c) => {
                      const active = !c.closed_at && new Date(c.expires_at) > new Date();
                      return (
                        <tr key={c.id}>
                          <td>{c.business_name || `#${c.business_id}`}</td>
                          <td className="plat-muted">{c.operator_email}</td>
                          <td>{c.purpose}</td>
                          <td className="plat-muted">{c.reason}</td>
                          <td><span style={statusStyle(active ? 'trial' : 'expired')}>{active ? fmtDate(c.expires_at) : 'Closed'}</span></td>
                          <td>{active && <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" onClick={() => onCloseCase(c.id)}>Close</button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'backups' && (
        <div className="plat-panel">
          <h3>Backup status</h3>
          {!backups ? (
            <p className="plat-muted">Loading…</p>
          ) : (
            <>
              <div className="plat-detail-row"><span className="label">Available</span><span>{backups.available ? 'Yes' : 'No'}</span></div>
              <div className="plat-detail-row"><span className="label">Local backup count</span><span>{backups.count ?? 0}</span></div>
              {backups.latest && (
                <div className="plat-detail-row"><span className="label">Latest</span><span>{backups.latest.filename || backups.latest.path || JSON.stringify(backups.latest)}</span></div>
              )}
              {backups.note && <p className="plat-muted" style={{ marginTop: 12 }}>{backups.note}</p>}
            </>
          )}
        </div>
      )}

      {tab === 'security' && (
        <>
          <div className="plat-panel">
            <h3>Production security</h3>
            {!security ? (
              <p className="plat-muted">Loading…</p>
            ) : (
              <>
                <div className="plat-detail-row">
                  <span className="label">Session timeout</span>
                  <span>{security.session_timeout_minutes} min inactivity</span>
                </div>
                <div className="plat-detail-row">
                  <span className="label">IP allowlist</span>
                  <span style={{ color: security.ip_allowlist_enabled ? '#34d399' : '#94a3b8' }}>
                    {security.ip_allowlist_enabled ? `${security.ip_allowlist_count} entries (SUPER_ADMIN_IP_ALLOWLIST)` : 'Disabled'}
                  </span>
                </div>
                <div className="plat-detail-row">
                  <span className="label">2FA required (env)</span>
                  <span style={{ color: security.require_2fa ? '#fbbf24' : '#94a3b8' }}>
                    {security.require_2fa ? 'SUPER_ADMIN_REQUIRE_2FA=true' : 'Optional'}
                  </span>
                </div>
                <div className="plat-detail-row">
                  <span className="label">Login alerts</span>
                  <span>{security.alert_emails_configured ? 'Configured (SUPER_ADMIN_ALERT_EMAILS)' : 'Not configured'}</span>
                </div>
                {security.require_2fa && !security.totp_enabled && (
                  <p style={{ color: '#fbbf24', marginTop: 12, fontSize: 13 }}>
                    Platform admin is blocked until you enable two-factor authentication below.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="plat-panel">
            <h3>Two-factor authentication (TOTP)</h3>
            <p className="plat-muted" style={{ marginTop: 0 }}>
              Use Google Authenticator, Authy, or similar. Required at login when enabled.
            </p>
            {security?.totp_enabled ? (
              <>
                <p style={{ color: '#34d399', margin: '0 0 12px' }}>2FA is enabled on your account.</p>
                <input
                  className="plat-search"
                  placeholder="6-digit code to disable"
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{ maxWidth: 200, marginBottom: 10 }}
                />
                <button type="button" className="plat-btn plat-btn-danger plat-btn-sm" disabled={securityBusy} onClick={onDisable2fa}>Disable 2FA</button>
              </>
            ) : twoFaSetup ? (
              <>
                <img src={twoFaSetup.qr_data_url} alt="2FA QR code" style={{ display: 'block', margin: '12px 0', width: 180, height: 180, background: '#fff', padding: 8, borderRadius: 8 }} />
                <p className="plat-muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>Manual key: {twoFaSetup.secret}</p>
                <input
                  className="plat-search"
                  placeholder="6-digit code to confirm"
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{ maxWidth: 200, margin: '10px 0' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="plat-btn plat-btn-primary plat-btn-sm" disabled={securityBusy} onClick={onEnable2fa}>Confirm & enable</button>
                  <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" onClick={() => { setTwoFaSetup(null); setTwoFaCode(''); }}>Cancel</button>
                </div>
              </>
            ) : (
              <button type="button" className="plat-btn plat-btn-primary plat-btn-sm" disabled={securityBusy} onClick={onStart2fa}>Set up 2FA</button>
            )}
          </div>

          <div className="plat-panel">
            <h3>Deployment notes</h3>
            <p className="plat-muted" style={{ marginTop: 0, lineHeight: 1.6 }}>
              Set environment variables on the backend: <code>SUPER_ADMIN_IP_ALLOWLIST</code> (comma-separated IPs/CIDR),
              {' '}<code>SUPER_ADMIN_REQUIRE_2FA=true</code>, <code>SUPER_ADMIN_ALERT_EMAILS</code>, and optionally
              {' '}<code>SUPER_ADMIN_SESSION_MINUTES</code> (default 15). For production, serve platform UI at
              {' '}<strong>admin.shahosuite.com</strong> via reverse proxy — same app, separate hostname.
            </p>
          </div>
        </>
      )}
    </>
  );
};

export default SystemPage;
